import React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PlusOutlined } from '@ant-design/icons'
import { TabsLayout } from '@app/layouts'

import { LntButton } from '@app/components/base'
import { Header, PermissionWrapper, SubheaderToolbarDivider } from '@app/components'
import { CommonQuotaDrawer } from '@app/drawers/CommonQuotaDrawer'
import { FileUploadModal, ACCEPT_FILE_TYPES } from '@app/modals/FileUploadModal'

import { FILE_TYPES } from '@app/api/constants'
import { BUTTON_TYPE, SIZE } from '@app/constants'
import { QUOTAS_REPORTS_KEY } from '@app/constants/queryKeys'
import { PERMISSION } from '@app/constants/permission'
import { exportTemplateQuotaReportFile } from '@app/utils/export'

import { useNotification, useToggle } from '@app/hooks'

import { createQuota, importQuotaReportFromFile } from '@app/api/routes/quotaReports'

import { isNull } from '@app/utils'

import { DirectoryTab, QuotaReportTab } from './Tabs'

import type { TabToolbars, TabsLayoutItem } from '@app/layouts/TabsLayout/types'
import type { CreateQuotaData } from '@app/api/routes/quotaReports/types'

const TABS_KEYS = {
  directoryTab: 'directoryTab',
  quotaReportTab: 'quotaReportTab'
} as const

const tabs: TabsLayoutItem[] = [
  {
    label: 'Справочник',
    key: TABS_KEYS.directoryTab,
    content: <DirectoryTab />
  },

  {
    label: 'Отчет по квотам',
    key: TABS_KEYS.quotaReportTab,
    content: <QuotaReportTab />
  }
]

const title = 'Управление квотами'

export const QuotaReportPage: React.FC = () => {
  const createQuotaDrawerToggle = useToggle()
  const importFromExcelToggle = useToggle()

  const notification = useNotification()
  const queryClient = useQueryClient()

  const createQuotaMutation = useMutation({
    mutationFn: createQuota,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUOTAS_REPORTS_KEY.quotaReportItems] })

      notification.success({
        message: 'Успешно:',
        description: 'Квота успешно создана'
      })
    }
  })

  const handleOnDownloadTemplateExcel = (): void => {
    // TODO уточнить название файла у Андрея З
    const fileName = 'Шаблон импорта'

    exportTemplateQuotaReportFile(fileName, { file: FILE_TYPES.xlsx })
  }

  const handleOpenImportFromExcelModal = (): void => {
    importFromExcelToggle.setToggled()
  }

  const handleCreateQuota = (data: CreateQuotaData): void => {
    createQuotaMutation.mutate(data, { onSettled: createQuotaDrawerToggle.setUntoggled })
  }

  const handleImportFromExcel = async (file: File | null): Promise<void> => {
    if (isNull(file)) return

    const pricesFormData = new FormData()

    pricesFormData.append('file', file, file.name)
    pricesFormData.append('fileFormat', FILE_TYPES.xlsx)

    await importQuotaReportFromFile(pricesFormData)

    await queryClient.invalidateQueries({ queryKey: [QUOTAS_REPORTS_KEY.quotaReportItems] })

    importFromExcelToggle.setUntoggled()
  }

  const tabsSubheaderToolbars: TabToolbars = {
    [TABS_KEYS.directoryTab]: [
      <PermissionWrapper
        key='change-from-excel'
        requiredPermissions={ [PERMISSION.editQuotas] }
      >
        <LntButton
          type={ BUTTON_TYPE.secondary }
          size={ SIZE.small }
          onClick={ handleOnDownloadTemplateExcel }
        >
          Шаблон импорта
        </LntButton>
      </PermissionWrapper>,

      <PermissionWrapper
        key='download'
        requiredPermissions={ [PERMISSION.editQuotas] }
      >
        <LntButton
          type={ BUTTON_TYPE.secondary }
          size={ SIZE.small }
          onClick={ handleOpenImportFromExcelModal }
        >
          Импорт квот
        </LntButton>

        <SubheaderToolbarDivider />
      </PermissionWrapper>,

      <PermissionWrapper
        key='new-list-button'
        requiredPermissions={ [PERMISSION.editQuotas] }
      >
        <LntButton
          icon={ <PlusOutlined /> }
          type={ BUTTON_TYPE.secondary }
          size={ SIZE.small }
          onClick={ createQuotaDrawerToggle.setToggled }
        >
          Новая квота
        </LntButton>
      </PermissionWrapper>
    ]
  }

  return (
    <>
      <Header controlProps={ { title } } />

      <TabsLayout
        tabs={ tabs }
        tabToolbars={ tabsSubheaderToolbars }
      />

      <CommonQuotaDrawer
        open={ createQuotaDrawerToggle.value }
        title='Создание квоты'
        onClose={ createQuotaDrawerToggle.setUntoggled }
        onSubmit={ handleCreateQuota }
      />

      <FileUploadModal
        title='Импорт из excel'
        accept={ ACCEPT_FILE_TYPES.xlsx }
        open={ importFromExcelToggle.value }
        onCancel={ importFromExcelToggle.setUntoggled }
        onFileUpload={ handleImportFromExcel }
      />
    </>
  )
}
